import React, { useState } from 'react';
import { LoginForm } from '../components/auth/LoginForm';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { Plus, X } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';

export const LoginView: React.FC = () => {
  const { subdomain, switchTenant } = useTenant();
  const { registerTenant, isLoading } = useAuth();

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [regForm, setRegForm] = useState({
    companyName: '',
    subdomain: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    tier: 'pro' as 'starter' | 'pro' | 'enterprise',
  });
  const [regError, setRegError] = useState<string | null>(null);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError(null);
    try {
      await registerTenant(regForm);
      setShowRegisterModal(false);
    } catch (err: any) {
      setRegError(err.message || 'Tenant registration failed');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div className="app-background-glow" />

      {/* Top Bar with Monorepo Brand & Tenant Switcher */}
      <header
        style={{
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'rgba(11, 15, 25, 0.6)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'var(--brand-gradient)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 800,
            }}
          >
            S
          </div>
          <div>
            <span style={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em' }}>SaaS Enterprise</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--brand-primary)', marginLeft: '8px', fontWeight: 600 }}>
              PWA Dashboard
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8125rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Tenant:</span>
            {['acme', 'globex', 'initech'].map((t) => (
              <button
                key={t}
                onClick={() => switchTenant(t)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-sm)',
                  background: subdomain === t ? 'var(--brand-primary-light)' : 'rgba(255, 255, 255, 0.05)',
                  border: subdomain === t ? '1px solid var(--brand-primary)' : '1px solid var(--border-subtle)',
                  color: subdomain === t ? '#a5b4fc' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {t}
              </button>
            ))}
          </div>

          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="w-3.5 h-3.5" />}
            onClick={() => setShowRegisterModal(true)}
          >
            New Tenant DB
          </Button>
        </div>
      </header>

      {/* Main Login Content */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <LoginForm />
      </main>

      {/* Footer Details */}
      <footer
        style={{
          textAlign: 'center',
          padding: '20px',
          borderTop: '1px solid var(--border-subtle)',
          fontSize: '0.8125rem',
          color: 'var(--text-muted)',
        }}
      >
        <span>Architected with MERN Stack • Dedicated MongoDB DB Isolation • Central Analytics Pipeline</span>
      </footer>

      {/* Provision New Tenant Modal */}
      {showRegisterModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
        >
          <div
            className="glass-panel"
            style={{
              width: '100%',
              maxWidth: '520px',
              padding: '32px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem' }}>Provision New Tenant Database</h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Creates an isolated MongoDB physical/logical DB and seeds root admin.
                </p>
              </div>
              <button
                onClick={() => setShowRegisterModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {regError && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-danger-bg)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#fca5a5',
                  fontSize: '0.8125rem',
                  marginBottom: '16px',
                }}
              >
                {regError}
              </div>
            )}

            <form onSubmit={handleRegisterSubmit}>
              <Input
                label="Company / Organization Name"
                placeholder="Cyberdyne Systems"
                required
                value={regForm.companyName}
                onChange={(e) => {
                  const companyName = e.target.value;
                  const autoSubdomain = companyName.toLowerCase().replace(/[^a-z0-9]/g, '');
                  setRegForm({ ...regForm, companyName, subdomain: autoSubdomain });
                }}
              />

              <Input
                label="Subdomain (Host Resolution)"
                placeholder="cyberdyne"
                required
                helperText={`Resolves to: ${regForm.subdomain || 'subdomain'}.saasapp.com -> tenant_${regForm.subdomain || 'subdomain'}_db`}
                value={regForm.subdomain}
                onChange={(e) => setRegForm({ ...regForm, subdomain: e.target.value.toLowerCase() })}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Input
                  label="Admin First Name"
                  placeholder="Sarah"
                  required
                  value={regForm.adminFirstName}
                  onChange={(e) => setRegForm({ ...regForm, adminFirstName: e.target.value })}
                />
                <Input
                  label="Admin Last Name"
                  placeholder="Connor"
                  required
                  value={regForm.adminLastName}
                  onChange={(e) => setRegForm({ ...regForm, adminLastName: e.target.value })}
                />
              </div>

              <Input
                label="Admin Email"
                type="email"
                placeholder="admin@cyberdyne.com"
                required
                value={regForm.adminEmail}
                onChange={(e) => setRegForm({ ...regForm, adminEmail: e.target.value })}
              />

              <Input
                label="Admin Password"
                type="password"
                placeholder="Minimum 8 characters"
                required
                value={regForm.adminPassword}
                onChange={(e) => setRegForm({ ...regForm, adminPassword: e.target.value })}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <Button type="button" variant="ghost" onClick={() => setShowRegisterModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Provision Tenant DB & Seed
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
